import { useEffect, useState, useRef } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Header from 'legacy/common/components/wysiwyg-form/Header';
import useManageProducts from 'legacy/events/hooks/useManageProducts';

import Tickets from './Tickets';
import FeesPaidBy from './FeesPaidBy';

import {
  MAX_COMPLIMENTARY_TICKETS,
  MAX_PRIVATE_TICKETS,
  MAX_PUBLIC_TICKETS
} from 'legacy/events/service';
import { useGetMe } from 'legacy/user/hooks';
import ComplimentaryTicketForm from './forms/ComplimentaryTicketForm';
import { useGetComplimentaryTickets } from '../../../hooks';
import ComplimentaryTickets from './ComplimentaryTickets';
import TicketForm from './forms/TicketForm';
import Sections from './Sections';
import { useEventEditorContext } from '../../../contexts/EventEditorContext';
import {
  IncomingCreateEventBundleProductDto,
  IncomingCreateEventStandardProductDto,
  IncomingUpdateEventBundleProductDto,
  IncomingUpdateEventStandardProductDto,
  OutgoingEventProductDto,
  OutgoingSectionDto
} from 'api/dtos/events/products';
import Spinner, { SpinnerType } from 'legacy/common/components/spinner/Spinner';
import { showError } from 'legacy/common/utils/toast';

const PUBLIC_TAB_INDEX = 0;
const PRIVATE_TAB_INDEX = 1;
const COMPLIMENTARY_TAB_INDEX = 2;

enum PageView {
  MAIN = 'MAIN',
  CREATE_PRODUCT = 'CREATE_PRODUCT',
  CREATE_PRODUCT_FOR_SECTION = 'CREATE_PRODUCT_FOR_SECTION',
  EDIT_PRODUCT = 'EDIT_PRODUCT',
  CREATE_COMPLIMENTARY = 'CREATE_COMPLIMENTARY'
}

const PricingTiers = () => {
  const {
    eventId,
    eventShareToken,
    refetchEvent,
    createDraft,
    isDraft,
    isEditing,
    isLoading: isEventLoading,
    lastUpdated,
    handleBack,
    addTierToSectionId,
    setAddTierToSectionId
  } = useEventEditorContext();

  const [tabIndex, setTabIndex] = useState(PUBLIC_TAB_INDEX);
  const { data: complimentaryTickets, refetch } =
    useGetComplimentaryTickets(eventId);

  // Use the enhanced useManageProducts hook
  const {
    products,
    sections,
    publicProducts,
    privateProducts,
    selectedProduct,
    selectedIndex,
    lastCreatedProductId,
    isLoading: isProductsLoading,
    setSelectedProduct,
    setSelectedIndex,
    addStandardProduct,
    addBundleProduct,
    updateStandardProduct,
    updateBundleProduct,
    deleteStandardProduct,
    deleteBundleProduct,
    toggleStandardProductActive,
    toggleStandardProductVisibility,
    toggleBundleProductActive,
    toggleBundleProductVisibility,
    addStandardProductToSection,
    addBundleProductToSection,
    reorderProducts,
    deleteSection
  } = useManageProducts(eventId);

  const { data: user } = useGetMe();
  const [currentPage, setCurrentPage] = useState<PageView>(PageView.MAIN);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const lastScrolledProductIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (addTierToSectionId) {
      setCurrentPage(PageView.CREATE_PRODUCT_FOR_SECTION);
    }
  }, [addTierToSectionId]);

  useEffect(() => {
    if (!eventId) {
      createDraft();
    }
  }, [eventId]); // ! Do not include createDraft in the dependency array - IT CAUSES INFINITE LOOP

  useEffect(() => {
    if (
      currentPage === PageView.MAIN &&
      lastCreatedProductId &&
      lastScrolledProductIdRef.current !== lastCreatedProductId
    ) {
      setTimeout(() => {
        const ticketElement = document.querySelector(
          `[data-ticket-id="${lastCreatedProductId}"]`
        );

        if (ticketElement) {
          window.scrollTo({
            top:
              ticketElement.getBoundingClientRect().top +
              window.pageYOffset -
              200,
            behavior: 'smooth'
          });

          lastScrolledProductIdRef.current = lastCreatedProductId;
        }
      }, 500);
    }
  }, [currentPage, lastCreatedProductId]);

  const navigateToCreateProduct = () => {
    setCurrentPage(PageView.CREATE_PRODUCT);
  };

  const navigateToCreateComplimentary = () => {
    setCurrentPage(PageView.CREATE_COMPLIMENTARY);
  };

  const navigateToMain = () => {
    setCurrentPage(PageView.MAIN);
    setSelectedProduct(null);
    setAddTierToSectionId(undefined);
  };

  const getCurrentProductType = () => {
    if (tabIndex === PUBLIC_TAB_INDEX) {
      return 'public';
    }

    if (tabIndex === PRIVATE_TAB_INDEX) {
      return 'private';
    }

    if (tabIndex === COMPLIMENTARY_TAB_INDEX) {
      return 'complimentary';
    }

    return null;
  };

  const currentProductType = getCurrentProductType();

  // Handle product CRUD operations using the hook functions
  const handleUpdateProduct = (values: OutgoingEventProductDto) => {
    if (!selectedProduct) {
      return;
    }

    if (
      selectedProduct.type === 'admission' ||
      selectedProduct.type === 'addon'
    ) {
      // Convert to IncomingUpdateEventStandardProductDto
      const updateDto: IncomingUpdateEventStandardProductDto = {
        name: values.name,
        description: values.description,
        price: values.price,
        isPrivate: values.isPrivate,
        isActive: values.isActive,
        isVisible: values.isVisible,
        imageUrl: values.imageUrl,
        startDate: values.startDate,
        endDate: values.endDate,
        maxQuantity: values.maxQuantity,
        purchaseRequirements: {
          requiresApproval:
            values.purchaseRequirements?.requiresApproval || false,
          questions: values.purchaseRequirements?.questions || [],
          requiredOrganizationIds:
            values.purchaseRequirements?.requiredOrganizationIds || [],
          requiredAssociationIds:
            values.purchaseRequirements?.requiredAssociationIds || []
        }
      };
      updateStandardProduct(selectedProduct.id, updateDto, {
        onSuccess: () => {
          refetchEvent();
          navigateToMain();
        },
        onError(error) {
          showError({
            title: 'Error updating product',
            message: error?.message
          });
        }
      });
    } else {
      // Convert to IncomingUpdateEventBundleProductDto
      const updateDto: IncomingUpdateEventBundleProductDto = {
        name: values.name,
        description: values.description,
        price: values.price,
        isPrivate: values.isPrivate,
        isActive: values.isActive,
        isVisible: values.isVisible,
        imageUrl: values.imageUrl,
        startDate: values.startDate,
        endDate: values.endDate,
        components: values.components || []
      };
      updateBundleProduct(selectedProduct.id, updateDto, {
        onSuccess: () => {
          refetchEvent();
          navigateToMain();
        },
        onError(error) {
          showError({
            title: 'Error updating bundle product',
            message: error?.message
          });
        }
      });
    }

    refetchEvent();
    navigateToMain();
  };

  const handleAddProduct = (values: OutgoingEventProductDto) => {
    if (values.type === 'admission' || values.type === 'addon') {
      const standardProductDto: IncomingCreateEventStandardProductDto = {
        type: values.type,
        name: values.name,
        description: values.description,
        price: values.price,
        isPrivate: values.isPrivate,
        isActive: values.isActive,
        imageUrl: values.imageUrl,
        startDate: values.startDate,
        endDate: values.endDate,
        maxQuantity: values.maxQuantity,
        purchaseRequirements: {
          requiresApproval:
            values.purchaseRequirements?.requiresApproval || false,
          questions: values.purchaseRequirements?.questions || [],
          requiredOrganizationIds:
            values.purchaseRequirements?.requiredOrganizationIds || [],
          requiredAssociationIds:
            values.purchaseRequirements?.requiredAssociationIds || []
        }
      };
      addStandardProduct(standardProductDto, {
        onSuccess: () => {
          refetchEvent();
          navigateToMain();
        },
        onError(error) {
          showError({
            title: 'Error adding product',
            message: error?.message
          });
        }
      });
    } else {
      const bundleProductDto: IncomingCreateEventBundleProductDto = {
        name: values.name,
        description: values.description,
        price: values.price,
        isPrivate: values.isPrivate,
        isActive: values.isActive,
        imageUrl: values.imageUrl,
        startDate: values.startDate,
        endDate: values.endDate,
        components: values.components || []
      };
      addBundleProduct(bundleProductDto, {
        onSuccess: () => {
          refetchEvent();
          navigateToMain();
        },
        onError(error) {
          showError({
            title: 'Error adding bundle product',
            message: error?.message
          });
        }
      });
    }
  };

  const handleAddProductToSection = (
    sectionId: string,
    values: OutgoingEventProductDto
  ) => {
    if (values.type === 'admission' || values.type === 'addon') {
      const standardProductDto: IncomingCreateEventStandardProductDto = {
        type: values.type as 'admission' | 'addon',
        name: values.name,
        description: values.description,
        price: values.price,
        isPrivate: values.isPrivate,
        isActive: values.isActive,
        imageUrl: values.imageUrl,
        startDate: values.startDate,
        endDate: values.endDate,
        maxQuantity: values.maxQuantity,
        purchaseRequirements: {
          requiresApproval:
            values.purchaseRequirements?.requiresApproval || false,
          questions: values.purchaseRequirements?.questions || [],
          requiredOrganizationIds:
            values.purchaseRequirements?.requiredOrganizationIds || [],
          requiredAssociationIds:
            values.purchaseRequirements?.requiredAssociationIds || []
        }
      };
      addStandardProductToSection(sectionId, standardProductDto, {
        onSuccess: () => {
          refetchEvent();
          setAddTierToSectionId(undefined);
          navigateToMain();
        },
        onError(error) {
          showError({
            title: 'Error adding product to section',
            message: error?.message
          });
        }
      });
    } else {
      const bundleProductDto: IncomingCreateEventBundleProductDto = {
        name: values.name,
        description: values.description,
        price: values.price,
        isPrivate: values.isPrivate,
        isActive: values.isActive,
        imageUrl: values.imageUrl,
        startDate: values.startDate,
        endDate: values.endDate,
        components: values.components || []
      };
      addBundleProductToSection(sectionId, bundleProductDto, {
        onSuccess: () => {
          refetchEvent();
          setAddTierToSectionId(undefined);
          navigateToMain();
        },
        onError(error) {
          showError({
            title: 'Error adding product to section',
            message: error?.message
          });
        }
      });
    }

    return true;
  };

  const handleRemoveProduct = () => {
    if (!selectedProduct) {
      return;
    }

    if (
      selectedProduct.type === 'admission' ||
      selectedProduct.type === 'addon'
    ) {
      deleteStandardProduct(selectedProduct.id, {
        onSuccess: () => {
          refetchEvent();
          navigateToMain();
        },
        onError(error) {
          showError({
            title: 'Error deleting product',
            message: error?.message
          });
          navigateToMain();
        }
      });
    } else {
      deleteBundleProduct(selectedProduct.id, {
        onSuccess: () => {
          refetchEvent();
          navigateToMain();
        },
        onError(error) {
          showError({
            title: 'Error deleting bundle product',
            message: error?.message
          });
          navigateToMain();
        }
      });
    }
  };

  const handleToggleProductActiveState = (
    productId: string,
    isActive: boolean
  ) => {
    const product = products.find((p) => p.id === productId);

    if (product && product.type === 'bundle') {
      toggleBundleProductActive(productId, isActive, {
        onSuccess: () => {
          refetchEvent();
        },
        onError(error) {
          showError({
            title: 'Error toggling bundle product status',
            message: error?.message
          });
        }
      });
    } else {
      toggleStandardProductActive(productId, isActive, {
        onSuccess: () => {
          refetchEvent();
        },
        onError(error) {
          showError({
            title: 'Error toggling standard product status',
            message: error?.message
          });
        }
      });
    }
  };

  const handleToggleProductVisibility = (productId: string) => {
    const product = products.find((p) => p.id === productId);

    if (product && product.type === 'bundle') {
      toggleBundleProductVisibility(productId, {
        onSuccess: () => {
          refetchEvent();
        },
        onError(error) {
          showError({
            title: 'Error toggling bundle product visibility',
            message: error?.message
          });
        }
      });
    } else {
      toggleStandardProductVisibility(productId, {
        onSuccess: () => {
          refetchEvent();
        },
        onError(error) {
          showError({
            title: 'Error toggling standard product visibility',
            message: error?.message
          });
        }
      });
    }
  };

  const handleReorderProducts = (
    tiers: Array<OutgoingEventProductDto>,
    sectionsData: Array<OutgoingSectionDto>
  ) => {
    // Convert from old format to new format
    const productsToReorder = products.map((product) => {
      const matchingTier = tiers.find((t) => t.id === product.id);
      if (!matchingTier) {
        return {
          productId: product.id,
          sortIndex: product.sortIndex || 0,
          sectionId: product.sectionId
        };
      }

      return {
        productId: product.id,
        sortIndex: matchingTier.sortIndex || 0,
        sectionId:
          matchingTier.sectionId === 'default'
            ? undefined
            : matchingTier.sectionId
      };
    });

    const sectionsToReorder = sectionsData.map((section) => ({
      id: section.id,
      sortIndex: section.sortIndex || 0
    }));

    reorderProducts(productsToReorder, sectionsToReorder, {
      onError(error) {
        showError({
          title: 'Error reordering products',
          message: error?.message
        });
      }
    });
  };

  const handleDeleteSection = (sectionId: string) => {
    deleteSection(sectionId, {
      onError(error) {
        showError({
          title: 'Error deleting section',
          message: error?.message
        });
      }
    });
  };

  const handleProductSelect = (
    product: OutgoingEventProductDto,
    index: number
  ) => {
    setSelectedIndex(index);
    setSelectedProduct(product);
    setCurrentPage(PageView.EDIT_PRODUCT);
  };

  if (!eventId || !products) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner type={SpinnerType.PRIMARY} />
      </div>
    );
  }

  // Render the main pricing tiers page
  const renderMainPage = () => (
    <>
      <Header
        isDraft={isDraft}
        isEditing={isEditing}
        title="Ticket Tiers"
        isLoading={isEventLoading || isProductsLoading}
        lastUpdated={lastUpdated}
        onBack={handleBack}
      />
      <div
        ref={mainContentRef}
        className="pt-[50px] md:pt-[180px] -mt-[90px] pb-20 max-w-[630px] md:max-w-[1128px] md:mx-auto flex justify-center overflow-x-hidden"
      >
        <div className="p-4 ">
          <div className="max-w-[630px] mx-auto">
            <FeesPaidBy />
          </div>

          <div className="py-6">
            <Tabs
              selectedIndex={tabIndex}
              onSelect={(index) => setTabIndex(index)}
            >
              <TabList className="flex text-center justify-between border-b text-base border-gray-100 text-gray-500 max-w-[630px] mx-auto md:mt-8">
                <Tab
                  className="w-full py-3 text-center cursor-pointer"
                  selectedClassName="text-primary-500 border-b-4 border-primary-400 "
                >
                  Public ({publicProducts.length}/{MAX_PUBLIC_TICKETS})
                </Tab>
                <Tab
                  className="w-full py-3 text-center cursor-pointer"
                  selectedClassName="text-primary-500 border-b-4 border-primary-400"
                >
                  Private ({privateProducts.length}/{MAX_PRIVATE_TICKETS})
                </Tab>
                <Tab
                  className="w-full py-3 text-center cursor-pointer"
                  selectedClassName="text-primary-500 border-b-4 border-primary-400"
                >
                  Comp ({complimentaryTickets?.items.length || 0}/
                  {MAX_COMPLIMENTARY_TICKETS})
                </Tab>
              </TabList>

              <TabPanel>
                <p className="pt-6 pb-4 font-normal text-base text-gray-500 max-w-[630px] mx-auto md:mb-12">
                  Public tiers are visible to everyone who visits the event
                  details page.
                </p>

                <Sections
                  eventId={eventId}
                  sections={sections}
                  tiers={publicProducts}
                  onReorder={handleReorderProducts}
                  onDeleteSection={handleDeleteSection}
                  addEventPricingTierToSection={handleAddProductToSection}
                  setAddTierToSectionId={setAddTierToSectionId}
                >
                  {(tickets, sectionId) => (
                    <Tickets
                      eventId={eventId}
                      eventShareToken={eventShareToken}
                      tickets={tickets}
                      selectedIndex={selectedIndex}
                      selectedTicket={selectedProduct || null}
                      toggleTicketTier={(id, isDisabled) =>
                        handleToggleProductActiveState(id, !isDisabled)
                      }
                      toggleHideTier={handleToggleProductVisibility}
                      setSelectedIndex={(id: string) => {
                        const foundProduct = products.find((p) => p.id === id);
                        const productIndex = products.findIndex(
                          (p) => p.id === id
                        );

                        if (foundProduct && productIndex !== -1) {
                          handleProductSelect(foundProduct, productIndex);
                        }
                      }}
                      setSelectedTicket={(ticket) => {
                        const foundProduct = products.find(
                          (p) => p.id === ticket.id
                        );
                        const productIndex = products.findIndex(
                          (p) => p.id === ticket.id
                        );

                        if (foundProduct && productIndex !== -1) {
                          handleProductSelect(foundProduct, productIndex);
                        }
                      }}
                      isSortable
                      sectionId={sectionId}
                      lastCreatedTicketId={lastCreatedProductId}
                    />
                  )}
                </Sections>

                <div className="mt-6 flex justify-center w-full fixed left-0 bottom-0 bg-white pb-8 z-50 md:pb-4 border-t-1 px-4 py-4 shadow-primary-50 shadow-top md:shadow-none">
                  <Button
                    type={ButtonType.Primary}
                    className="w-full md:max-w-[343px] !bg-[#3853C4] text-white"
                    disabled={publicProducts.length >= MAX_PUBLIC_TICKETS}
                    onClick={navigateToCreateProduct}
                  >
                    Create Public Tier
                  </Button>
                </div>
              </TabPanel>

              <TabPanel>
                <p className="pt-6 font-normal text-base text-gray-500 max-w-[630px] mx-auto md:mb-12">
                  Private tiers are not visible on the event details page.
                  Guests can join only with the specific private code,
                  accessible by clicking pricing post-event creation.
                </p>

                <Tickets
                  eventId={eventId}
                  eventShareToken={eventShareToken}
                  tickets={privateProducts}
                  selectedIndex={selectedIndex}
                  selectedTicket={selectedProduct || null}
                  toggleTicketTier={(id, isDisabled) =>
                    handleToggleProductActiveState(id, !isDisabled)
                  }
                  toggleHideTier={handleToggleProductVisibility}
                  setSelectedIndex={(id: string) => {
                    const foundProduct = products.find((p) => p.id === id);
                    const productIndex = products.findIndex((p) => p.id === id);

                    if (foundProduct && productIndex !== -1) {
                      handleProductSelect(foundProduct, productIndex);
                    }
                  }}
                  setSelectedTicket={(ticket) => {
                    const foundProduct = products.find(
                      (p) => p.id === ticket.id
                    );
                    const productIndex = products.findIndex(
                      (p) => p.id === ticket.id
                    );

                    if (foundProduct && productIndex !== -1) {
                      handleProductSelect(foundProduct, productIndex);
                    }
                  }}
                  lastCreatedTicketId={lastCreatedProductId}
                />

                <div className="mt-6 flex justify-center w-full fixed left-0 bottom-0 bg-white pb-8 z-50 md:pb-4 border-t-1 px-4 py-4 shadow-primary-50 shadow-top md:shadow-none">
                  <Button
                    type={ButtonType.Primary}
                    className="w-full md:max-w-[343px] !bg-[#3853C4] text-white"
                    disabled={privateProducts.length >= MAX_PRIVATE_TICKETS}
                    onClick={navigateToCreateProduct}
                  >
                    Add Private Product
                  </Button>
                </div>
              </TabPanel>

              <TabPanel>
                {!eventId ? (
                  <p className="pt-6 font-normal text-base text-red-500 text-center max-w-[630px] mx-auto md:mb-12">
                    Please add an event title before creating complimentary
                    tickets.
                  </p>
                ) : (
                  <>
                    <p className="pt-6 font-normal text-base text-gray-500 max-w-[630px] mx-auto md:mb-12">
                      Complimentary tickets contribute to the event&apos;s max
                      capacity and appear as unclaimed on the guest list. Hosts
                      and co-hosts receive individual PDFs via email for
                      printing.
                    </p>

                    <ComplimentaryTickets
                      tickets={complimentaryTickets?.items}
                    />

                    <div className="mt-6 flex justify-center w-full fixed left-0 bottom-0 bg-white pb-8 z-50 md:pb-4 border-t-1 px-4 py-4 shadow-primary-50 shadow-top md:shadow-none">
                      <Button
                        type={ButtonType.Primary}
                        className="w-full md:max-w-[343px] !bg-[#3853C4] text-white"
                        disabled={
                          complimentaryTickets?.items?.length >=
                            MAX_COMPLIMENTARY_TICKETS || !eventId
                        }
                        onClick={navigateToCreateComplimentary}
                      >
                        Add Complimentary Ticket
                      </Button>
                    </div>
                  </>
                )}
              </TabPanel>
            </Tabs>
          </div>
        </div>
      </div>
    </>
  );

  // Render based on current page state
  switch (currentPage) {
    case PageView.CREATE_PRODUCT:
      return (
        <TicketForm
          user={user}
          onSubmit={handleAddProduct}
          onCancel={navigateToMain}
          isTierPrivate={currentProductType === 'private'}
          isDraft={isDraft}
          isEditing={isEditing}
          isLoading={isEventLoading || isProductsLoading}
          lastUpdated={lastUpdated}
        />
      );
    case PageView.CREATE_PRODUCT_FOR_SECTION:
      return (
        <TicketForm
          user={user}
          onSubmit={(values) =>
            addTierToSectionId &&
            handleAddProductToSection(addTierToSectionId, values)
          }
          onCancel={() => {
            navigateToMain();
            setAddTierToSectionId(undefined);
          }}
          isTierPrivate={false}
          isDraft={isDraft}
          isEditing={isEditing}
          isLoading={isEventLoading || isProductsLoading}
          lastUpdated={lastUpdated}
          formTitle="Add Tier to Section"
        />
      );
    case PageView.EDIT_PRODUCT:
      return (
        <TicketForm
          user={user}
          ticket={selectedProduct || null}
          onSubmit={handleUpdateProduct}
          onCancel={navigateToMain}
          onRemove={handleRemoveProduct}
          isTierPrivate={currentProductType === 'private'}
          isDraft={isDraft}
          isEditing={isEditing}
          isLoading={isEventLoading || isProductsLoading}
          lastUpdated={lastUpdated}
        />
      );
    case PageView.CREATE_COMPLIMENTARY:
      return (
        <ComplimentaryTicketForm
          eventId={eventId}
          refetchTickets={refetch}
          onCancel={navigateToMain}
          isDraft={isDraft}
          isEditing={isEditing}
          isLoading={isEventLoading || isProductsLoading}
          lastUpdated={lastUpdated}
        />
      );
    case PageView.MAIN:
    default:
      return renderMainPage();
  }
};

export default PricingTiers;
